import * as React from 'react';
import MuiBox from '@mui/material/Box';
import { resolveIcon } from './iconMap';

export interface IconProps {
  name?: string;
  size?: 'mini' | 'tiny' | 'small' | 'large' | 'big' | 'huge' | 'massive';
  color?: string;
  loading?: boolean;
  disabled?: boolean;
  link?: boolean;
  onClick?: React.MouseEventHandler;
  className?: string;
  style?: React.CSSProperties;
  fitted?: boolean;
  [k: string]: unknown;
}

const SIZE_TO_FONT: Record<
  NonNullable<IconProps['size']>,
  'inherit' | 'small' | 'medium' | 'large'
> = {
  mini: 'small',
  tiny: 'small',
  small: 'small',
  large: 'medium',
  big: 'large',
  huge: 'large',
  massive: 'large',
};

/**
 * Renders a Semantic-style <Icon name="x" /> as an MUI icon.
 * Local replacement for the previous shim Icon — kept because the codebase
 * still uses string-based icon names. New code should import the MUI icon
 * directly.
 */
// `props` is annotated explicitly: IconProps has a string index signature, which makes
// forwardRef's PropsWithoutRef<> collapse the named props away.
const Icon = React.forwardRef<HTMLSpanElement, IconProps>(function Icon(
  props: IconProps,
  ref: React.ForwardedRef<HTMLSpanElement>
) {
  const {
    name,
    size,
    color,
    loading,
    disabled,
    link,
    onClick,
    className,
    style,
    fitted: isFitted,
    ...rest
  } = props;
  void rest;
  const Cmp = resolveIcon(name);
  const fontSize = size ? SIZE_TO_FONT[size] : 'small';
  const cursor = link || onClick ? 'pointer' : undefined;
  const marginRight = isFitted ? 0 : undefined;
  return (
    <MuiBox
      {...rest}
      ref={ref}
      component="span"
      onClick={disabled ? undefined : onClick}
      className={['ui', 'icon', name, className].filter(Boolean).join(' ')}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        verticalAlign: 'middle',
        opacity: disabled ? 0.5 : 1,
        cursor,
        animation: loading ? 'spin 2s linear infinite' : undefined,
        '@keyframes spin': {
          from: { transform: 'rotate(0deg)' },
          to: { transform: 'rotate(360deg)' },
        },
        color,
        marginRight,
        ...style,
      }}
    >
      <Cmp fontSize={fontSize} />
    </MuiBox>
  );
});

export default Icon;
